import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { generateUEID } from '@components/Toast/utils'

type TooltipCloseButton = {
  onClose: () => void;
}

type TooltipButton = {
  text: string;
  onClick: () => void;
}

export interface TooltipOptions {
  id?: string;
  target: string;
  atBottom?: boolean;
  fixed?: boolean;
  onDisplayed?: () => void;
  onDismissed?: () => void;
  text?: string;
  closeButton?: TooltipCloseButton;
  primaryButton?: TooltipButton;
  secondaryButton?: TooltipButton;
}

export interface TooltipConfig {
  id: string
  options: TooltipOptions
}

interface TooltipStates {
  queue: TooltipConfig[],
  push: (options: TooltipOptions) => TooltipConfig
  remove: (id: string) => void
}

export const TooltipContext = createContext<TooltipStates>({
  queue: [],
  push: () => undefined,
  remove: () => undefined
})

const useTooltip = () => useContext(TooltipContext)

export default useTooltip

const dismissTooltip = (tooltipId: string, queue: TooltipConfig[]) => {
  const index = queue.findIndex(t => t.id === tooltipId)
  if (index !== -1) {
    queue.splice(index, 1)
  }

  return [...queue]
}

export const useTooltipStates = (): TooltipStates => {
  const [queue, setQueue] = useState([])
  const ref = useRef(queue)

  const push = useCallback((options: TooltipOptions) => {
    const nextId = options.id || generateUEID()
    const q = ref.current
    const index = q.findIndex(t => t.id === nextId)
    const tooltip: TooltipConfig = {
      id: nextId,
      options
    }
    if (index !== -1) {
      console.warn('[Duplicated] duplicated tooltip id')
    } else {
      options.id = nextId // sync id
      ref.current = [...q, tooltip]
      setQueue(ref.current)
    }

    return tooltip
  }, [])

  const remove = useCallback((id: string) => {
    ref.current = dismissTooltip(id, ref.current)
    setQueue(ref.current)
  }, [setQueue])

  return useMemo(() => ({
    queue,
    push,
    remove
  }), [queue, push, remove])
}
